import React from 'react';
import { MapPin, Users, Share2, Calendar, Globe, Shield, Smartphone, Zap } from 'lucide-react';

const Features = () => {
  const features = [
    {
      icon: <MapPin className="h-12 w-12 text-blue-600" />,
      title: 'Interactive Maps',
      description: 'Plan your routes with integrated Google Maps. Search locations, get directions, and visualize your entire journey.',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: <Users className="h-12 w-12 text-purple-600" />,
      title: 'Real-time Collaboration',
      description: 'Invite friends and family to plan together. See live updates as everyone contributes to the itinerary.',
      color: 'from-purple-500 to-pink-500'
    },
    {
      icon: <Share2 className="h-12 w-12 text-green-600" />,
      title: 'Easy Sharing',
      description: 'Share your travel plans with public links. Inspire others with your amazing itineraries.',
      color: 'from-green-500 to-emerald-500'
    },
    {
      icon: <Calendar className="h-12 w-12 text-orange-600" />,
      title: 'Day-wise Planning',
      description: 'Organize your trip day by day. Add places, activities, timings, and notes for each day.',
      color: 'from-orange-500 to-red-500'
    },
    {
      icon: <Globe className="h-12 w-12 text-indigo-600" />,
      title: 'Global Destinations',
      description: 'Plan trips anywhere in the world. Our platform supports destinations across all continents.',
      color: 'from-indigo-500 to-blue-500'
    },
    {
      icon: <Shield className="h-12 w-12 text-teal-600" />,
      title: 'Secure & Private',
      description: 'Your travel plans are protected with enterprise-grade security. Control who sees your trips.',
      color: 'from-teal-500 to-cyan-500'
    },
    {
      icon: <Smartphone className="h-12 w-12 text-pink-600" />,
      title: 'Mobile Friendly',
      description: 'Access your trips on any device. Responsive design ensures great experience everywhere.',
      color: 'from-pink-500 to-rose-500'
    },
    {
      icon: <Zap className="h-12 w-12 text-yellow-600" />,
      title: 'Lightning Fast',
      description: 'Built for speed and performance. Real-time updates and instant synchronization across devices.',
      color: 'from-yellow-500 to-orange-500'
    }
  ];

  return (
    <div className="min-h-screen py-20">
      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 text-center mb-20 animate-fade-in">
        <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
          Powerful Features for
          <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent"> Perfect Trips</span>
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
          Discover all the tools you need to plan, collaborate, and share amazing travel experiences. 
          From interactive maps to real-time collaboration, we've got you covered.
        </p>
      </div>

      {/* Features Grid */}
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
          {features.map((feature, index) => (
            <div 
              key={index} 
              className="group relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-500 border border-gray-100 animate-slide-up"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className={`absolute inset-0 bg-gradient-to-r ${feature.color} opacity-0 group-hover:opacity-5 rounded-3xl transition-opacity duration-300`}></div>
              
              <div className="relative z-10">
                <div className="mb-6 transform group-hover:scale-110 transition-transform duration-300">
                  {feature.icon}
                </div>
                
                <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-gray-800">
                  {feature.title}
                </h3>
                
                <p className="text-gray-600 leading-relaxed group-hover:text-gray-700">
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA Section */}
      <div className="max-w-4xl mx-auto px-4 text-center mt-20 animate-bounce-in">
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-12 text-white shadow-2xl">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Start Your Journey?
          </h2>
          <p className="text-xl mb-8 text-blue-100">
            Join thousands of travelers who trust TripForge for their adventures.
          </p>
          <div className="space-x-4">
            <button className="bg-white text-blue-600 px-8 py-4 rounded-xl font-semibold hover:bg-gray-100 transform hover:-translate-y-1 transition-all duration-300 shadow-lg">
              Get Started Free
            </button>
            <button className="border-2 border-white text-white px-8 py-4 rounded-xl font-semibold hover:bg-white hover:text-blue-600 transform hover:-translate-y-1 transition-all duration-300">
              View Demo
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Features;